import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { fetchPhoneticAtlas } from "@/lib/api";
import KanaWithOrigins from "@/components/KanaWithOrigins";

/**
 * Phonetic Atlas — every phonetic driver in the curated dataset, grouped
 * by reading in gojuon order. A learner who's encountered せい six times
 * this week can land on the せい section and see every driver producing
 * it (青 → 6 kanji, 成 → 5, 生 → 5, 正 → 4, 制 → 3 …) with the actual
 * member kanji as tappable tiles.
 *
 * Layout: sticky gojuon-row chip strip at the top doubles as a jump-nav.
 * Sections below render lazily-ish (just plain DOM — ~445 driver cards
 * total, well within budget for a single-page render).
 */
export default function PhoneticAtlas({ onSelectKanji, embedded = false }) {
  const q = useQuery({
    queryKey: ["phonetic-atlas"],
    queryFn: fetchPhoneticAtlas,
    staleTime: Infinity,
  });

  const [filter, setFilter] = useState(""); // restrict to one reading substring
  const [showGhostOnly, setShowGhostOnly] = useState(false);
  const sectionRefs = useRef({});

  const data = q.data;

  const filteredGroups = useMemo(() => {
    if (!data) return [];
    const norm = filter.trim().toLowerCase();
    if (!norm) {
      // No filter — apply ghost-only and return.
      return data.groups
        .map((g) => ({
          ...g,
          readings: g.readings
            .map((r) => ({
              ...r,
              drivers: showGhostOnly ? r.drivers.filter((d) => d.is_ghost) : r.drivers,
            }))
            .filter((r) => r.drivers.length > 0),
        }))
        .filter((g) => g.readings.length > 0);
    }
    // Match against ANY of: reading (hiragana), driver glyph, driver
    // meaning (English), or member kanji. A learner who types '青' or
    // 'blue' or 'sei' (we don't auto-romaji yet, but hiragana works) or
    // a member glyph like '清' should all converge on the right section.
    const matchDriver = (drv) => {
      if (drv.char === norm) return true;
      if ((drv.meaning || "").toLowerCase().includes(norm)) return true;
      if (drv.members.includes(norm)) return true;
      return false;
    };
    return data.groups
      .map((g) => ({
        ...g,
        readings: g.readings
          .map((r) => {
            const readingHits = r.reading.includes(norm);
            const drivers = (showGhostOnly ? r.drivers.filter((d) => d.is_ghost) : r.drivers)
              .filter((d) => readingHits || matchDriver(d));
            return { ...r, drivers };
          })
          .filter((r) => r.drivers.length > 0),
      }))
      .filter((g) => g.readings.length > 0);
  }, [data, filter, showGhostOnly]);

  const jumpTo = (initial) => {
    const el = sectionRefs.current[initial];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (q.isLoading) {
    return (
      <div className={`text-sumiSoft font-sans text-sm animate-pulse ${embedded ? "" : "px-6 md:px-12 py-10"}`}>
        loading the atlas…
      </div>
    );
  }

  // Embedded mode (inside the Home page's BROWSE → Phonetic mode) skips
  // the outer page padding and the H1 header — BuildPath already owns
  // the page chrome and mode-toggle strip, so duplicating it would just
  // create redundant headers.
  const Wrapper = embedded ? "div" : "div";
  const wrapperClass = embedded
    ? ""
    : "px-4 md:px-12 py-8 max-w-7xl mx-auto";

  return (
    <Wrapper className={wrapperClass} data-testid="atlas-page">
      {!embedded && (
        <header className="mb-6">
          <h1 className="font-serif text-3xl md:text-5xl text-sumi mb-2">Phonetic Atlas</h1>
          <p className="font-sans text-sm text-sumiSoft max-w-3xl leading-relaxed">
            Every phonetic driver in the dataset — {data.totals.unique_drivers} unique
            components producing {data.totals.driver_count} family-bonds across{" "}
            {data.totals.reading_count} readings. Includes both <strong>canonical drivers</strong>
            {" "}(青, 寺, 工, 方…) and <strong>etymological ghosts</strong> (𢦏, 𠦝, 俞, 龹…) —
            the historical 形声 phonetics not in the modern decomposition.
          </p>
        </header>
      )}

      {/* Filter + sticky gojuon jump-nav */}
      <div className="sticky top-[57px] md:top-[72px] z-20 bg-washi/95 backdrop-blur-sm border-y border-borderDefault py-2 -mx-4 md:-mx-12 px-4 md:px-12 mb-6">
        <div className="flex items-center gap-3 flex-wrap mb-2">
          <input
            data-testid="atlas-filter"
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="filter — reading (せい), driver (青), meaning (blue), or member (清)…"
            lang="ja"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            className="font-sans text-sm bg-transparent border border-borderDefault px-3 py-1.5 w-80 placeholder:text-sumiSoft focus:outline-none focus:border-shu"
          />
          <label className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-sans text-sumi cursor-pointer">
            <input
              type="checkbox"
              checked={showGhostOnly}
              onChange={(e) => setShowGhostOnly(e.target.checked)}
              data-testid="atlas-ghost-only"
              className="accent-shu"
            />
            ghosts only
          </label>
          <span className="text-[10px] uppercase tracking-[0.25em] text-sumiSoft font-sans">
            {filteredGroups.reduce((s, g) => s + g.readings.length, 0)} readings ·{" "}
            {filteredGroups.reduce((s, g) => s + g.readings.reduce((sr, r) => sr + r.drivers.length, 0), 0)} drivers
          </span>
        </div>
        <div className="flex flex-wrap gap-1" data-testid="atlas-gojuon-nav">
          {data.groups.map((g) => (
            <button
              key={g.initial}
              data-testid={`atlas-jump-${g.initial}`}
              onClick={() => jumpTo(g.initial)}
              className="font-serif text-base text-sumi border border-borderDefault px-2.5 py-1 hover:bg-sumi hover:text-washi transition-colors min-w-[36px]"
              title={`${g.reading_count} readings · ${g.driver_count} drivers`}
            >
              {g.initial}
            </button>
          ))}
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-8">
        {filteredGroups.map((g) => (
          <section
            key={g.initial}
            ref={(el) => (sectionRefs.current[g.initial] = el)}
            data-testid={`atlas-section-${g.initial}`}
            className="scroll-mt-32"
          >
            <h2 className="font-serif text-2xl md:text-4xl text-shu border-b border-borderDefault pb-1 mb-4">
              <KanaWithOrigins text={g.initial} />
              <span className="ml-3 text-[10px] uppercase tracking-[0.25em] text-sumiSoft font-sans align-middle">
                {g.reading_count} readings · {g.driver_count} drivers
              </span>
            </h2>
            <div className="space-y-5">
              {g.readings.map((r) => (
                <div key={r.reading} data-testid={`atlas-reading-${r.reading}`}>
                  <h3 className="font-serif text-lg text-sumi mb-2 sticky top-[150px] bg-washi/90 backdrop-blur-sm py-1 z-10">
                    <KanaWithOrigins text={r.reading} className="text-shu" />
                    <span className="ml-3 text-[10px] uppercase tracking-[0.25em] text-sumiSoft font-sans">
                      {r.drivers.length} driver{r.drivers.length === 1 ? "" : "s"}
                    </span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {r.drivers.map((d) => (
                      <DriverCard
                        key={`${d.char}-${r.reading}`}
                        driver={d}
                        onSelectKanji={onSelectKanji}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
        {filteredGroups.length === 0 && (
          <p className="text-sumiSoft font-sans text-sm italic" data-testid="atlas-empty">
            no readings match — try a partial like こ or せ.
          </p>
        )}
      </div>
    </Wrapper>
  );
}

function DriverCard({ driver, onSelectKanji }) {
  // The whole card is a link to the relevant phonetic page:
  //   • Canonical drivers (青, 寺, 工…) → /kanji/{driver}?tab=phonetic
  //   • Ghost drivers (𢦏, 𠦝, 俞…) have no own card, so we deep-link
  //     into the FIRST member's phonetic tab — the family is the same
  //     tree on either side, so a learner who taps the 𢦏 card lands
  //     on 哉's phonetic view with the same family rendered.
  // Member tiles and the big driver glyph stay independently clickable
  // via the overlay-link pattern: the full-card link sits behind the
  // foreground content as an `absolute inset-0` <Link>; the foreground
  // tiles use `relative z-10` so their own <Link> intercepts the click
  // first. This keeps right-click "open in new tab" working for both
  // layers, which a plain onClick handler would silently break.
  const cardLinkTarget = driver.is_kanji
    ? `/kanji/${encodeURIComponent(driver.char)}?tab=phonetic`
    : driver.members.length
      ? `/kanji/${encodeURIComponent(driver.members[0])}?tab=phonetic`
      : null;

  const handleSpaInterception = (e, targetChar) => {
    // Honour the in-app SPA navigator when present (sets focus state etc);
    // fall through to browser nav on cmd/ctrl-click and middle-click so
    // 'open in new tab' Just Works.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    if (onSelectKanji) {
      e.preventDefault();
      onSelectKanji(targetChar);
    }
  };

  return (
    <motion.div
      layout
      data-testid={`atlas-driver-${driver.char}`}
      className="relative border border-borderDefault p-3 bg-washi/50 hover:bg-washi hover:border-shu/50 transition-colors group"
    >
      {/* Full-card link sits behind the foreground content. Children with
          their own <Link>+`relative z-10` capture clicks first. */}
      {cardLinkTarget && (
        <Link
          to={cardLinkTarget}
          data-testid={`atlas-driver-link-${driver.char}`}
          aria-label={`open phonetic family for ${driver.char} (${driver.meaning || "unnamed"})`}
          className="absolute inset-0 z-0"
        />
      )}

      <div className="relative flex items-start gap-3 mb-2 pointer-events-none">
        {driver.is_kanji ? (
          <Link
            to={`/kanji/${encodeURIComponent(driver.char)}`}
            onClick={(e) => handleSpaInterception(e, driver.char)}
            className="relative z-10 pointer-events-auto font-serif text-4xl text-sumi leading-none shrink-0 hover:text-shu transition-colors"
            title={`open ${driver.char} card`}
            data-testid={`atlas-driver-glyph-${driver.char}`}
          >
            {driver.char}
          </Link>
        ) : (
          <span
            className="font-serif text-4xl text-sumi leading-none shrink-0"
            title="etymological driver — no own card"
          >
            {driver.char}
          </span>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-serif text-sm text-sumi leading-tight line-clamp-2 group-hover:text-shu transition-colors">
            {driver.meaning || <em className="text-sumiSoft">unnamed</em>}
          </p>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            <span className="text-[9px] uppercase tracking-[0.18em] text-sumiSoft font-sans">
              {driver.count} kanji
            </span>
            {driver.is_ghost && (
              <span className="text-[9px] uppercase tracking-[0.18em] text-sumiSoft border border-borderSoft px-1.5 py-px">
                ghost
              </span>
            )}
            {driver.jlpt && (
              <span className="text-[9px] uppercase tracking-[0.18em] text-sumiSoft border border-borderSoft px-1.5 py-px">
                {driver.jlpt}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="relative flex flex-wrap gap-1 mt-2 pointer-events-none">
        {driver.sample.map((m) => (
          <Link
            key={m.kanji}
            to={`/kanji/${encodeURIComponent(m.kanji)}`}
            onClick={(e) => handleSpaInterception(e, m.kanji)}
            data-testid={`atlas-member-${driver.char}-${m.kanji}`}
            className="relative z-10 pointer-events-auto flex flex-col items-center justify-center w-12 h-14 border border-borderDefault bg-washi hover:border-shu transition-colors"
            title={`${m.meaning}${m.jlpt ? ` · ${m.jlpt}` : ""}`}
          >
            <span className="font-serif text-2xl text-sumi leading-none">{m.kanji}</span>
            <span className="text-[8px] uppercase tracking-wider text-sumiSoft font-sans mt-0.5 truncate w-full text-center px-0.5">
              {m.meaning.split(/[\s,/]/)[0]}
            </span>
          </Link>
        ))}
        {driver.members.length > driver.sample.length && (
          <div className="flex items-center justify-center w-12 h-14 border border-dashed border-borderDefault text-[10px] uppercase tracking-wider text-sumiSoft font-sans">
            +{driver.members.length - driver.sample.length}
          </div>
        )}
      </div>
    </motion.div>
  );
}
